import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'; // useCallback kept for SalesMapController
import PropTypes from 'prop-types';
import { Card, Col, Form, Row, Table } from 'react-bootstrap';
import L from 'leaflet';
import {
  CircleMarker,
  MapContainer,
  Polygon,
  Popup,
  TileLayer,
  useMapEvents
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const formatNumber = value => Number(value || 0).toLocaleString();

const formatAmount = (value, currencyId) => {
  const numericValue = Number(value || 0);
  if (!currencyId) {
    return numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyId,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericValue);
  } catch (error) {
    return `${currencyId} ${numericValue.toFixed(0)}`;
  }
};

const clampRatio = value => Math.max(0, Math.min(1, Number(value || 0)));

const getHeatColor = ratio => {
  const normalized = clampRatio(ratio);
  const hue = 210 - normalized * 200;
  return `hsl(${hue}, 80%, 46%)`;
};

const getVisibleStats = (points, bounds) => {
  const initialValue = {
    visibleSalesWeight: 0,
    visibleLocationsCount: 0,
    bounds: bounds
      ? {
          south: bounds.getSouth(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          east: bounds.getEast()
        }
      : null
  };

  if (!bounds) {
    return points.reduce(
      (accumulator, point) => ({
        visibleSalesWeight: accumulator.visibleSalesWeight + Number(point.orders_count || 0),
        visibleLocationsCount: accumulator.visibleLocationsCount + 1
      }),
      initialValue
    );
  }

  return points.reduce((accumulator, point) => {
    if (!bounds.contains([point.latitude, point.longitude])) {
      return accumulator;
    }

    return {
      visibleSalesWeight: accumulator.visibleSalesWeight + Number(point.orders_count || 0),
      visibleLocationsCount: accumulator.visibleLocationsCount + 1
    };
  }, initialValue);
};

const SalesMapController = ({ points, onViewportChange, autoFitKey }) => {
  const [lastAutoFitKey, setLastAutoFitKey] = useState('');
  const updateViewportStats = useCallback(
    mapInstance => {
      const bounds = mapInstance?.getBounds?.();
      if (!bounds) return;
      onViewportChange(getVisibleStats(points, bounds));
    },
    [onViewportChange, points]
  );

  const map = useMapEvents({
    moveend() {
      updateViewportStats(map);
    },
    zoomend() {
      updateViewportStats(map);
    }
  });

  useEffect(() => {
    if (!map) return;
    const timer = setTimeout(() => {
      map.invalidateSize({ animate: false });
    }, 50);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (!map || !points.length) return;
    const normalizedAutoFitKey = String(autoFitKey || '');
    if (normalizedAutoFitKey && normalizedAutoFitKey === lastAutoFitKey) {
      return;
    }

    map.invalidateSize({ animate: false });

    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 11, {
        animate: false
      });
      updateViewportStats(map);
      return;
    }

    const bounds = L.latLngBounds(points.map(point => [point.latitude, point.longitude]));
    map.fitBounds(bounds, {
      padding: [28, 28],
      maxZoom: 12,
      animate: false
    });
    updateViewportStats(map);
    if (normalizedAutoFitKey) {
      setLastAutoFitKey(normalizedAutoFitKey);
    }
  }, [autoFitKey, lastAutoFitKey, map, points, updateViewportStats]);

  return null;
};

const SalesLocationMapCard = ({ topLocations, regional, currencyId, t }) => {
  const [regionLevel, setRegionLevel] = useState('state');
  const ALLOWED_REGION_LEVELS = ['country', 'state'];

  const normalizedTopLocations = useMemo(() => topLocations || [], [topLocations]);

  const regionalPayload = useMemo(() => (regional && typeof regional === 'object' ? regional : {}), [regional]);
  const regionalLevels = useMemo(() => {
    if (Array.isArray(regionalPayload.levels_available) && regionalPayload.levels_available.length) {
      return regionalPayload.levels_available;
    }
    return ['country', 'state', 'city'].filter(level => Array.isArray(regionalPayload[level]) && regionalPayload[level].length);
  }, [regionalPayload]);

  const regionalRows = useMemo(() => {
    const rows = regionalPayload?.[regionLevel];
    return Array.isArray(rows) ? rows : [];
  }, [regionalPayload, regionLevel]);

  const choroplethRows = useMemo(
    () => regionalRows.filter(row => Number.isFinite(Number(row?.latitude)) && Number.isFinite(Number(row?.longitude))),
    [regionalRows]
  );

  const choroplethAreaRows = useMemo(
    () =>
      regionalRows.filter(row => {
        const shape = Array.isArray(row?.shape) && row.shape.length >= 3;
        const shapeMulti = Array.isArray(row?.shape_multi) && row.shape_multi.some(ring => Array.isArray(ring) && ring.length >= 3);
        return shape || shapeMulti;
      }),
    [regionalRows]
  );

  const choroplethViewportPoints = useMemo(() => {
    const shapePoints = choroplethAreaRows.map(row => {
      const firstMultiRing = Array.isArray(row.shape_multi) && row.shape_multi.length ? row.shape_multi[0] : null;
      const first = (firstMultiRing || row.shape || [])[0] || [];
      return {
        latitude: Number(first[0]),
        longitude: Number(first[1]),
        orders_count: Number(row.orders_count || 0)
      };
    }).filter(point => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));

    if (shapePoints.length) {
      return shapePoints;
    }

    return choroplethRows.map(row => ({
      latitude: Number(row.latitude),
      longitude: Number(row.longitude),
      orders_count: Number(row.orders_count || 0)
    }));
  }, [choroplethAreaRows, choroplethRows]);

  const maxRegionalIncome = useMemo(
    () => choroplethRows.reduce((maxValue, row) => Math.max(maxValue, Number(row?.total_amount || 0)), 0),
    [choroplethRows]
  );

  useEffect(() => {
    if (!regionalLevels.length) return;
    if (!regionalLevels.includes(regionLevel)) {
      setRegionLevel(regionalLevels[0]);
    }
  }, [regionalLevels, regionLevel]);

  const choroplethAutoFitKey = useMemo(
    () => `choropleth:${regionLevel}:${regionalRows.map(row => String(row.region_key || row.region || '')).join('|')}`,
    [regionLevel, regionalRows]
  );

  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
          <div>
            <h5 className="mb-1">{t('DASHBOARD_HOME.SECTIONS.SALES_MAP')}</h5>
            <div className="text-600 fs--1">{t('DASHBOARD_HOME.SECTIONS.SALES_MAP_HELP')}</div>
          </div>
        </div>

        {normalizedTopLocations.length || regionalRows.length ? (
          <Row className="g-3">
            <Col xs={12}>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Form.Select
                  size="sm"
                  style={{ maxWidth: 280 }}
                  value={regionLevel}
                  onChange={({ target }) => setRegionLevel(target.value)}
                >
                  {regionalLevels.filter(level => ALLOWED_REGION_LEVELS.includes(level)).map(level => (
                    <option key={level} value={level}>
                      {t(`ORDERS.ANALISIS.MAP.REGION_LEVEL.${String(level || '').toUpperCase()}`)}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>

            <Col xs={12}>
              {(choroplethRows.length || choroplethAreaRows.length) ? (
                <div className="border rounded overflow-hidden" style={{ height: 520 }} dir="ltr">
                  <MapContainer
                    center={[-34.6037, -58.3816]}
                    zoom={4}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <SalesMapController
                      points={choroplethViewportPoints}
                      onViewportChange={() => {}}
                      autoFitKey={choroplethAutoFitKey}
                    />
                    {choroplethAreaRows.map(row => {
                      const totalIncome = Number(row.total_amount || 0);
                      const ratio = maxRegionalIncome > 0 ? totalIncome / maxRegionalIncome : 0;
                      const color = getHeatColor(ratio);
                      const polygonRings =
                        Array.isArray(row.shape_multi) && row.shape_multi.length
                          ? row.shape_multi
                          : [row.shape];
                      const normalizedRings = polygonRings
                        .map(ring =>
                          (ring || [])
                            .map(point => [Number(point[0]), Number(point[1])])
                            .filter(point => Number.isFinite(point[0]) && Number.isFinite(point[1]))
                        )
                        .filter(ring => ring.length >= 3);

                      if (!normalizedRings.length) {
                        return null;
                      }

                      return (
                        <Fragment key={`region-shape-${row.region_key}`}>
                          {normalizedRings.map((positions, ringIndex) => (
                            <Polygon
                              key={`region-shape-${row.region_key}-${ringIndex}`}
                              positions={positions}
                              pathOptions={{
                                color,
                                fillColor: color,
                                fillOpacity: 0.36,
                                weight: 2
                              }}
                            >
                              <Popup>
                                <div className="fw-semi-bold mb-1">{row.region}</div>
                                <div className="fs--1">
                                  {t('DASHBOARD_HOME.TABLE.SALES_COUNT')}: {formatNumber(row.orders_count || 0)}
                                </div>
                                <div className="fs--1">
                                  {t('DASHBOARD_HOME.TABLE.TOTAL')}: {formatAmount(row.total_amount, currencyId)}
                                </div>
                                <div className="fs--1">
                                  {t('ORDERS.ANALISIS.MAP.TABLE.SHIPPING_COST')}: {formatAmount(row.shipping_cost_total, currencyId)}
                                </div>
                                <div className="fs--1">
                                  {t('ORDERS.ANALISIS.MAP.TABLE.OTHER_COST')}: {formatAmount(row.other_cost_total, currencyId)}
                                </div>
                              </Popup>
                            </Polygon>
                          ))}
                        </Fragment>
                      );
                    })}
                    {choroplethRows.map(row => {
                      const hasAreaShape =
                        (Array.isArray(row?.shape) && row.shape.length >= 3) ||
                        (Array.isArray(row?.shape_multi) && row.shape_multi.some(ring => Array.isArray(ring) && ring.length >= 3));
                      if (hasAreaShape) {
                        return null;
                      }
                      if (regionLevel === 'state') {
                        return null;
                      }
                      const totalIncome = Number(row.total_amount || 0);
                      const ratio = maxRegionalIncome > 0 ? totalIncome / maxRegionalIncome : 0;
                      const color = getHeatColor(ratio);
                      const radius = 8 + Math.round(Math.min(18, Math.sqrt(Number(row.orders_count || 0))));

                      return (
                        <CircleMarker
                          key={`choropleth-${row.region_key}`}
                          center={[Number(row.latitude), Number(row.longitude)]}
                          radius={radius}
                          pathOptions={{
                            color,
                            fillColor: color,
                            fillOpacity: 0.55,
                            weight: 2
                          }}
                        >
                          <Popup>
                            <div className="fw-semi-bold mb-1">{row.region}</div>
                            <div className="fs--1">
                              {t('DASHBOARD_HOME.TABLE.SALES_COUNT')}: {formatNumber(row.orders_count || 0)}
                            </div>
                            <div className="fs--1">
                              {t('DASHBOARD_HOME.TABLE.TOTAL')}: {formatAmount(row.total_amount, currencyId)}
                            </div>
                            <div className="fs--1">
                              {t('ORDERS.ANALISIS.MAP.TABLE.SHIPPING_COST')}:{' '}
                              {formatAmount(row.shipping_cost_total, currencyId)}
                            </div>
                            <div className="fs--1">
                              {t('ORDERS.ANALISIS.MAP.TABLE.OTHER_COST')}:{' '}
                              {formatAmount(row.other_cost_total, currencyId)}
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </div>
              ) : (
                <div
                  className="border rounded d-flex align-items-center justify-content-center text-center text-600 px-4"
                  style={{ height: 520 }}
                >
                  {t('DASHBOARD_HOME.EMPTY.SALES_MAP')}
                </div>
              )}
            </Col>

            {regionalRows.length ? (
              <Col xs={12}>
                <Table responsive className="mb-0 fs--1">
                  <thead className="bg-light">
                    <tr>
                      <th>{t('ORDERS.ANALISIS.MAP.TABLE.REGION')}</th>
                      <th className="text-end">{t('DASHBOARD_HOME.TABLE.SALES_COUNT')}</th>
                      <th className="text-end">{t('DASHBOARD_HOME.TABLE.TOTAL')}</th>
                      <th className="text-end">{t('ORDERS.ANALISIS.MAP.TABLE.INCOME_SHARE')}</th>
                      <th className="text-end">{t('ORDERS.ANALISIS.MAP.TABLE.SHIPPING_COST')}</th>
                      <th className="text-end">{t('ORDERS.ANALISIS.MAP.TABLE.OTHER_COST')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionalRows.slice(0, 25).map(row => (
                      <tr key={`regional-${row.region_key}`}>
                        <td className="fw-semi-bold">{row.region}</td>
                        <td className="text-end">{formatNumber(row.orders_count || 0)}</td>
                        <td className="text-end">{formatAmount(row.total_amount, currencyId)}</td>
                        <td className="text-end">{Number(row.income_share_pct || 0).toFixed(2)}%</td>
                        <td className="text-end">{formatAmount(row.shipping_cost_total, currencyId)}</td>
                        <td className="text-end">{formatAmount(row.other_cost_total, currencyId)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            ) : null}
          </Row>
        ) : (
          <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES_MAP')}</div>
        )}
      </Card.Body>
    </Card>
  );
};

SalesMapController.propTypes = {
  points: PropTypes.arrayOf(PropTypes.object).isRequired,
  onViewportChange: PropTypes.func.isRequired,
  autoFitKey: PropTypes.string
};

SalesMapController.defaultProps = {
  autoFitKey: ''
};

SalesLocationMapCard.propTypes = {
  topLocations: PropTypes.arrayOf(PropTypes.object),
  regional: PropTypes.object,
  currencyId: PropTypes.string,
  t: PropTypes.func.isRequired
};

SalesLocationMapCard.defaultProps = {
  topLocations: [],
  regional: {},
  currencyId: ''
};

export default SalesLocationMapCard;
